<template>
  <div class="newsroom-media-slider-fi">
    <grid-container-fi>
      <div :class="['fi-col-span-full lg:fi-col-start-2', 'md:fi-col-end-12 lg:fi-col-end-12']">
        <div class="newsroom-media-slider-fi__content">
          <div
            v-if="isSlider"
            class="newsroom-media-slider-fi__head"
          >
            <div class="newsroom-media-slider-fi__headline">
              {{ $dict.get('newsroom.Detail-Page.Images-Headline') }}
            </div>

            <div class="newsroom-media-slider-fi__slider-controls">
              <div class="newsroom-media-slider-fi__slider-arrows">
                <button
                  type="button"
                  :class="{
                    'newsroom-media-slider-fi__slider-prev': true,
                    'newsroom-media-slider-fi__slider--inactive': sliders.forwardDisabled,
                  }"
                  :aria-disabled="sliders.forwardDisabled"
                  @click="prevSlide"
                >
                  <arrow-icon />
                </button>
                <button
                  type="button"
                  :class="{
                    'newsroom-media-slider-fi__slider-next': true,
                    'newsroom-media-slider-fi__slider--inactive': sliders.backDisabled,
                  }"
                  :aria-disabled="sliders.backDisabled"
                  @click="nextSlide"
                >
                  <arrow-icon />
                </button>
              </div>
            </div>
          </div>

          <carousel-fi
            v-if="images.length"
            ref="carousel"
            :class="{
              'newsroom-media-slider-fi__slider': true,
              'newsroom-media-slider-fi__slider--no-slider': !isSlider,
            }"
            :settings="sliderSettings"
            theme="media-items"
            no-arrows
            clipped-right
            @after-change="onAfterChange"
          >
            <carousel-slide-fi
              v-for="(image, indexImage) in images"
              :key="indexImage"
              theme="media-items"
            >
              <div class="newsroom-media-slider-fi__slide">
                <media-library-item-fi
                  :item="image"
                  disable-dialog
                  is-newsroom
                  @show-zoom-dialog="onShowZoomDialog"
                />
              </div>
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </div>
    </grid-container-fi>
    <media-library-zoom-image-dialog-fi
      ref="mediaLibraryZoomImageDialog"
      :item="item"
      is-newsroom
    />
  </div>
</template>

<script setup lang="ts">
import { ArrowIcon, CarouselFi, CarouselSlideFi } from 'atoms';
import { computed, onMounted, ref, useTemplateRef } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import MediaLibraryItemFi from '@/components/media-library-item-fi/MediaLibraryItemFi.vue';
import MediaLibraryZoomImageDialogFi from '@/components/media-library-zoom-image-dialog-fi/MediaLibraryZoomImageDialogFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFields } from '@/composables/fields';
import screenWidths from '@/tailwind/screenWidths';

defineOptions({ name: 'NewsroomMediaSliderFi' });

const fields = useFields('NewsroomDetailFiFields');
const { isLG, isMD } = useBreakpoints();

const carousel = useTemplateRef('carousel');
const mediaLibraryZoomImageDialog = useTemplateRef('mediaLibraryZoomImageDialog');

const currentSlide = ref(0);
const item = ref<any>(null);
const isMounted = ref(false);

const images = computed(() => fields.images ?? []);

const sliderSettings = computed(() => ({
  rewind: false,
  pagination: false,
  perPage: 1,
  perMove: 1,
  destroy: false,
  gap: 24,
  noDrag: 'input, .no-drag',
  breakpoints: {
    [screenWidths.sm]: {
      perPage: 2,
      destroy: false,
    },
    [screenWidths.md]: {
      perPage: 3,
      destroy: false,
    },
    [screenWidths.lg]: {
      perPage: 5,
      destroy: false,
    },
  },
}));

const visibleSlidesCount = computed(() => (isLG.value ? 5 : 3));

const sliders = computed(() => ({
  forwardDisabled: currentSlide.value === 0,
  backDisabled: currentSlide.value + visibleSlidesCount.value >= images.value.length,
}));

const isSlider = computed(() => {
  if (!isMounted.value) {
    return false;
  }
  return images.value.length > visibleSlidesCount.value && isMD.value;
});

onMounted(() => {
  isMounted.value = true;
});

const nextSlide = () => {
  if (carousel.value) {
    if (currentSlide.value + visibleSlidesCount.value === images.value.length) {
      return;
    }
    (carousel.value as any).next();
  }
};

const prevSlide = () => {
  if (carousel.value) {
    if (currentSlide.value === 0) {
      return;
    }
    (carousel.value as any).prev();
  }
};

const onAfterChange = ({ newSlideIndex }: { newSlideIndex: number }) => {
  currentSlide.value = newSlideIndex;
};

const onShowZoomDialog = (zoomItem: any) => {
  item.value = zoomItem;
  (mediaLibraryZoomImageDialog.value as any).show();
};
</script>

<style scoped src="./newsroom-media-slider-fi" />
